const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

let scriptLoadPromise = null;
let currentNonce = null;

export function getGoogleClientId() {
  return String(process.env.VUE_APP_GOOGLE_CLIENT_ID || "").trim();
}

export function isGoogleIdentityConfigured() {
  return getGoogleClientId().length > 0;
}

export function isLocalGoogleIdentityRuntime() {
  if (typeof window === "undefined") return false;
  const hostname = String(window.location.hostname || "").toLowerCase();
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1"
  );
}

export function shouldUseGoogleIdentity() {
  return false;
}

export function loadGoogleIdentityScript() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.google?.accounts?.id) return Promise.resolve(true);
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${GIS_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener(
        "load",
        () => resolve(Boolean(window.google?.accounts?.id)),
        { once: true }
      );
      if (window.google?.accounts?.id) resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(Boolean(window.google?.accounts?.id));
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export async function generateGoogleNonce() {
  const nonce = btoa(
    String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32)))
  );
  const encodedNonce = new TextEncoder().encode(nonce);
  const hashBuffer = await crypto.subtle.digest("SHA-256", encodedNonce);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashedNonce = hashArray
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
  currentNonce = nonce;
  return { nonce, hashedNonce };
}

export function getCurrentGoogleNonce() {
  return currentNonce;
}

export async function initializeGoogleIdentity({
  clientId,
  callback,
  hashedNonce,
}) {
  const loaded = await loadGoogleIdentityScript();
  if (!loaded || !window.google?.accounts?.id) return false;

  window.google.accounts.id.initialize({
    client_id: clientId,
    callback,
    nonce: hashedNonce,
    use_fedcm_for_prompt: true,
    auto_select: true,
    itp_support: true,
  });
  return true;
}

export async function promptGoogleOneTap() {
  const loaded = await loadGoogleIdentityScript();
  if (!loaded || !window.google?.accounts?.id) return;
  window.google.accounts.id.prompt();
}

export async function renderGoogleSignInButton(element, options = {}) {
  const loaded = await loadGoogleIdentityScript();
  if (!loaded || !window.google?.accounts?.id || !element) return false;

  const width = options.width || 320;
  window.google.accounts.id.renderButton(element, {
    type: "standard",
    shape: "pill",
    theme: "outline",
    text: "continue_with",
    size: "large",
    logo_alignment: "left",
    width,
    ...options,
  });
  return true;
}
